import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type TileStatus = "not_started" | "locked" | "in_progress" | "complete";

interface AssessmentModuleRow {
  module_id: string;
  status: string;
}

export const PREREQUISITE_RULES: Record<string, string[]> = {
  "owner-readiness": [],
  "owner-readiness-exit-timeline": ["owner-readiness"],
  "business-profile": ["owner-readiness"],
  "business-valuation": ["owner-readiness-exit-timeline", "business-profile"],
  "business-optimization": ["business-valuation"],
  "advisory-team": ["business-optimization"],
  "financial-readiness": ["business-optimization"],
  "successor-strategy": ["business-optimization"],
  "governance-family": ["business-optimization"],
  "exit-structure": ["business-optimization"],
  "mysuccession-plan": [
    "owner-readiness",
    "owner-readiness-exit-timeline",
    "business-profile",
    "business-valuation",
    "business-optimization",
    "advisory-team",
    "financial-readiness",
    "successor-strategy",
    "governance-family",
    "exit-structure",
  ],
};

export function useDashboardAssessments() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-assessment-modules", user?.id],
    queryFn: async () => {
      const { data: modules, error } = await supabase
        .from("assessment_modules")
        .select("module_id, status")
        .eq("user_id", user!.id);

      if (error) {
        console.error("[useDashboardAssessments]", error.message);
        return [] as AssessmentModuleRow[];
      }
      return (modules ?? []) as AssessmentModuleRow[];
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const modules = data ?? [];
  const statusMap = new Map(modules.map((m) => [m.module_id, m.status]));

  const completedCount = modules.filter((m) => m.status === "complete").length;

  function getTileStatus(moduleId: string): TileStatus {
    const dbStatus = statusMap.get(moduleId);

    // Check prerequisites
    const prereqs = PREREQUISITE_RULES[moduleId] ?? [];
    const prereqsMet = prereqs.every((p) => statusMap.get(p) === "complete");

    if (!prereqsMet) return "locked";
    if (dbStatus === "complete") return "complete";
    if (dbStatus === "in-progress" || dbStatus === "in_progress") return "in_progress";
    return "not_started";
  }

  // Find first incomplete module in sequence for "Start now" CTA
  const SEQUENCE = [
    "owner-readiness",
    "owner-readiness-exit-timeline",
    "business-profile",
    "business-valuation",
    "business-optimization",
    "advisory-team",
    "financial-readiness",
    "successor-strategy",
    "governance-family",
    "exit-structure",
    "mysuccession-plan",
  ];

  const firstIncompleteModule = SEQUENCE.find(
    (id) => statusMap.get(id) !== "complete"
  );

  // Status of the first incomplete module (for label logic)
  const firstIncompleteStatus: TileStatus = firstIncompleteModule
    ? getTileStatus(firstIncompleteModule)
    : "not_started";

  function getIncompletePrereqs(moduleId: string): string[] {
    const prereqs = PREREQUISITE_RULES[moduleId] ?? [];
    return prereqs.filter((p) => statusMap.get(p) !== "complete");
  }

  /**
   * Find the next incomplete module in sequence, explicitly excluding
   * a given module_id (e.g. the one just completed but possibly still
   * cached as incomplete).
   */
  function getNextIncompleteModule(excludeModuleId?: string): string | null {
    // Build an effective status map that treats the excluded module as complete
    const effectiveComplete = (id: string) =>
      id === excludeModuleId || statusMap.get(id) === "complete";

    for (const id of SEQUENCE) {
      if (effectiveComplete(id)) continue;

      // Check prerequisites using the effective status
      const prereqs = PREREQUISITE_RULES[id] ?? [];
      const prereqsMet = prereqs.every((p) => effectiveComplete(p));
      if (!prereqsMet) continue;

      return id;
    }
    return null;
  }

  return {
    getTileStatus,
    getIncompletePrereqs,
    getNextIncompleteModule,
    completedCount,
    totalAssessments: 11,
    isLoading,
    firstIncompleteModule: firstIncompleteModule ?? SEQUENCE[0],
    firstIncompleteStatus,
  };
}
